/** The public marketing landing page for DNR.
 *
 * SCOPE
 * -----
 * Rendered directly by `App.tsx` for the unauthenticated root routes - the empty
 * hash, `/#/`, and `/#` - never through `Router`, `AppShell`, `SyncCoordinator`,
 * and never beneath a `QueryClientProvider`. It stays fully presentational:
 * no react-query hooks, no database or sync reads, no authenticated chrome.
 * Every CTA is a real hash link so keyboard navigation, middle-click, and
 * "copy address" all work. All routes, auth behaviour, and business logic are
 * untouched.
 *
 * TYPOGRAPHY
 * ----------
 * Tailwind reads `--font-sans` = `'Manrope', 'Segoe UI', system-ui, ...`. No
 * Google Fonts import is added on purpose (it would break `verify:offline` and
 * the offline build check), so the effective face is the clean system-ui stack.
 * We carry the hierarchy through size, weight, letter-spacing and line-height
 * instead, exactly to the brief's desktop/mobile scale.
 *
 * PALETTE - additive Tailwind tokens in `src/ui/index.css`
 *   midnight #080F1D · secondary dark #111B2B · elevated #162235
 *   deep teal #163C43 · teal accent #70D9D0 · champagne gold #C5A46D
 *   warm ivory #F7F5EF · primary light #F7F9FC · secondary light #B8C3D4
 *   slate ink #172033 · royal blue #2868E8 · deep navy #0B1C32
 *
 * RHYTHM
 *   1. Premium dark navy hero with an ambient radial glow + teal highlight.
 *   2. Warm-ivory feature cards.
 *   3. Rich dark navy business showcase with document/product mockups.
 *   4. Light workflow (Billing → Inventory → Invoices → Business Management),
 *      horizontal connectors that become a vertical stack on mobile.
 *   5. Distinctive deep-blue-to-teal gradient "free to use for now" band.
 *   6. Refined dark footer.
 *
 * All sections use one 8px spacing scale and a centred `max-w-6xl` content
 * container, with section padding 80-104px desktop / 52-68px mobile.
 */

import { useState } from 'react';
import {
  ArrowRight,
  CheckCheck,
  ClipboardList,
  IndianRupee,
  FileText,
  Archive,
  LayoutGrid,
  Mail,
  Menu,
  Package,
  ReceiptIndianRupee,
  Sparkles,
  Store,
  TrendingUp,
  Users,
  WifiOff,
  X,
} from 'lucide-react';

/* ---------------------------------------------------------------------------
 * Content payload - verified against the routed app screens in src/app/router.
 * --------------------------------------------------------------------------- */

const FEATURES = [
  {
    icon: FileText,
    title: 'Professional Invoices',
    description: 'Create, manage, preview, and print GST-compliant invoices.',
  },
  {
    icon: Package,
    title: 'Inventory Management',
    description: 'Track stock, products, and inventory across locations.',
  },
  {
    icon: IndianRupee,
    title: 'Billing & Payments',
    description: 'Record payments, track balances, and manage receivables.',
  },
] as const;

const BENEFITS = [
  { icon: IndianRupee, label: 'Business-document management', note: 'Keep invoices, quotations, and delivery notes in one place.' },
  { icon: ReceiptIndianRupee, label: 'Invoices and quotations', note: 'Lead and track orders from first quote to final invoice.' },
  { icon: TrendingUp, label: 'Payment tracking', note: 'Record payments and follow outstanding invoice balances.' },
  { icon: WifiOff, label: 'Local and offline use', note: 'Work inside the shop, on the floor, and on the road.' },
] as const;

const BUSINESSES = [
  { label: 'Retail shops', icon: Store },
  { label: 'Hardware businesses', icon: Package },
  { label: 'Interior designers', icon: LayoutGrid },
  { label: 'Carpenters and furniture businesses', icon: Archive },
  { label: 'Other small businesses', icon: Users },
] as const;

/** The four document types the app can produce (shown as the showcase panel). */
const DOCUMENT_TYPES = [
  { icon: ReceiptIndianRupee, label: 'Tax Invoice' },
  { icon: ClipboardList, label: 'Quotation' },
  { icon: Package, label: 'Sales Order' },
  { icon: Store, label: 'Delivery Note' },
] as const;

/* ---------------------------------------------------------------------------
 * Reusable class strings so button, badge and text colours stay identical and
 * stay in sync with the design tokens in `src/ui/index.css`.
 * --------------------------------------------------------------------------- */


/* ── 1 · Premium navigation ─────────────────────────── */
function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Navigation() {
  const [open, setOpen] = useState(false);
  const go = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(id);
  };
  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="flex h-20 items-center border-b border-white/[0.06] bg-ink/80 px-4 sm:px-6">
        <a href="#/" className="flex shrink-0 items-center gap-2.5" aria-label="DNR home">
          <img src="/assets/logo.svg" alt="DNR logo" className="h-10 w-10 rounded-xl" />
          <span className="text-sm font-extrabold tracking-tight text-fog sm:text-lg">DNR</span>
          <span className="text-xs font-medium text-gold uppercase tracking-wider hidden sm:inline-block">DIGITAL NET REGISTER</span>
        </a>
        <nav className="ml-auto hidden items-center gap-2.5 sm:gap-5 md:flex" aria-label="Top navigation">
          <button type="button" onClick={() => scrollToSection('features')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Features</button>
          <button type="button" onClick={() => scrollToSection('business')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Businesses</button>
          <button type="button" onClick={() => scrollToSection('workflow')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Products</button>
          <a href="#/login" className="text-sm font-medium text-mist transition-colors hover:text-fog">Sign In</a>
          <a href="#/signup" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-bold text-ink transition-colors hover:bg-[#b89766]">
            Get Started
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        </nav>
        <button
          type="button"
          className="absolute right-3 rounded p-2 text-mist transition-colors hover:bg-white/[0.06] sm:absolute sm:right-4"
          aria-label="Open menu"
          onClick={() => setOpen((v) => !v)}
        >
          <Menu className="h-5 w-5" aria-hidden />
        </button>
      </div>
      {open && (
        <div className="border-b border-white/[0.06] bg-ink/95 px-4 pb-6 pt-3 sm:hidden">
          <nav className="flex flex-col gap-4" aria-label="Mobile">
            <a className="text-left text-sm font-medium text-mist transition-colors hover:text-fog" href="#features" onClick={go('features')}>Features</a>
            <a className="text-left text-sm font-medium text-mist transition-colors hover:text-fog" href="#business" onClick={go('business')}>Businesses</a>
            <a className="text-left text-sm font-medium text-mist transition-colors hover:text-fog" href="#workflow" onClick={go('workflow')}>Products</a>
            <a className="text-left text-sm font-medium text-mist transition-colors hover:text-fog" href="#/login">Sign In</a>
            <a className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-[#b89766]" href="#/signup">
              Get Started
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <button type="button" className="mt-2 rounded p-2 text-mist transition-colors hover:bg-white/[0.06]" aria-label="Close menu" onClick={() => setOpen(false)}>
              <X className="h-5 w-5" aria-hidden />
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}


/* ── 2 · Hero ────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden bg-ink pt-20">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(112,217,208,0.16),transparent_60%),radial-gradient(ellipse_at_20%_80%,rgba(40,104,232,0.12),transparent_55%)]" aria-hidden="true" />
      </div>
      <div className="relative mx-auto flex flex-col items-center gap-8 px-4 pb-12 pt-10 sm:px-6 sm:pt-8 md:flex-row md:items-start md:gap-12 md:pb-16 md:pt-12 lg:px-8">
        <div className="w-full max-w-xl flex-1">
          <div className="flex items-center gap-2">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Your Business. Digitally Managed.
            </p>
          </div>
          <h1 className="mt-6 text-3xl font-extrabold leading-[1.05] tracking-tight text-fog sm:text-4xl md:text-5xl lg:text-6xl">
            DNR
            <span className="text-gold"> — Digital Net Register</span>
          </h1>
          <p className="mt-5 max-w-lg text-sm leading-6 text-mist sm:text-base">
            Billing · Inventory · Invoices · Business Management — all in one place.
          </p>
          <div className="mt-6 flex flex-col gap-2.5 sm:mt-8 sm:flex-row sm:items-center sm:gap-3">
            <a href="#/signup" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-ink transition-colors hover:bg-[#b89766] sm:px-7 sm:py-3.5">
              Get Started
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <button type="button" onClick={() => scrollToSection('features')} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] px-6 py-3 text-sm font-bold text-fog transition-colors hover:border-white/[0.2] hover:text-fog sm:px-7 sm:py-3.5">
              Explore Features
            </button>
          </div>
          <p className="mt-5 text-sm leading-6 text-mist/70">
            No credit card, no commitment. Get started with the current version at no cost — some
            features may still be in development, and we will communicate any future pricing
            changes before they take effect.
          </p>
        </div>


        <div className="relative mx-auto w-full max-w-xl flex-none">
          <div className="absolute -inset-3 md:-inset-4 rounded-2xl bg-gradient-to-br from-teal-900/40 to-ink/40 blur-2xl" aria-hidden="true" />
          <div className="relative rounded-2xl border border-white/[0.1] bg-ivory p-2.5 shadow-2xl shadow-ink/20 sm:p-3 md:p-4">
            <div className="flex items-center justify-between border-b border-slate-300/15 pb-2 sm:pb-3">
              <div className="flex items-center gap-2">
                <img src="/assets/logo.svg" alt="DNR" className="h-5 w-5 rounded sm:h-7 sm:w-7" />
                <span className="text-xs font-bold text-ink sm:text-sm">DNR</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="rounded-full bg-gold/20 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-ink sm:text-[10px]">Demo</span>
                <span className="text-[10px] font-medium text-mist sm:text-xs">Invoice</span>
              </div>
            </div>
            <p className="mt-2 text-[9px] font-medium uppercase tracking-[0.12em] text-mist sm:mt-3 sm:text-[10px]">Invoice</p>
            <p className="text-[10px] font-bold text-ink sm:text-xs">#DEMO-001</p>
            <p className="mt-0.5 text-[9px] text-mist sm:mt-1 sm:text-[10px]">From: XYZ Traders, Example City</p>
            <div className="mt-3 space-y-1 border-t border-slate-300/15 pt-2 text-[10px] sm:mt-4 sm:text-xs">
              <div className="flex justify-between"><span className="text-mist">Bill to</span><span className="font-medium text-ink">ABC Traders, Sample City</span></div>
              <div className="flex justify-between"><span className="text-mist">Date</span><span className="font-medium text-ink">01 Oct 2026</span></div>
            </div>
            <table className="mt-2 w-full text-[9px] sm:mt-3 sm:text-xs">
              <thead>
                <tr className="border-b border-slate-300/15 text-[8px] font-bold uppercase tracking-wider text-mist">
                  <th className="py-1 pr-1.5 text-right sm:py-2 sm:pr-3">Item</th>
                  <th className="py-1 pr-1.5 text-right sm:py-2 sm:pr-3">Qty</th>
                  <th className="py-1 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-300/10">
                  <td className="py-1 pr-1.5 text-ink">Sample Goods</td>
                  <td className="py-1 pr-1.5 text-right">10</td>
                  <td className="py-1 text-right text-ink">₹10,000.00</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-2 flex justify-between border-t border-slate-300/10 py-1.5 text-[9px] sm:mt-3 sm:py-2 sm:text-xs">
              <span className="text-mist">Taxable value</span>
              <span className="text-ink">₹10,000.00</span>
            </div>
            <div className="flex justify-between border-b border-slate-300/10 pb-1 text-[8px] text-mist sm:text-[10px]">
              <span>Intra-state supply</span>
              <span>CGST + SGST</span>
            </div>
            <div className="mt-1 flex justify-between py-0.5 text-[9px] sm:mt-2 sm:text-xs">
              <span className="text-mist">CGST (9%)</span>
              <span className="text-ink">₹900.00</span>
            </div>
            <div className="flex justify-between border-b border-slate-300/10 pb-1 text-[9px] sm:text-xs">
              <span className="text-mist">SGST (9%)</span>
              <span className="text-ink">₹900.00</span>
            </div>
            <div className="mt-1 flex justify-between border-t border-slate-300/10 py-2 text-xs font-bold sm:mt-2 sm:py-3">
              <span className="text-ink">Total</span>
              <span className="text-gold">₹11,800.00</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[8px] font-medium text-mist sm:text-[10px]">Status</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-teal-900/40 px-2 py-0.5 text-[8px] font-semibold text-teal">
                <CheckCheck className="h-2.5 w-2.5" aria-hidden />
                Sample
              </span>
            </div>
          </div>
          <p className="mx-auto mt-2 max-w-md text-center text-[8px] leading-4 text-mist/70 sm:mt-3 sm:text-[10px]">
            Illustrative sample for demonstration purposes only. Business names, locations,
            invoice details, and amounts are fictional. Actual invoice particulars and GST
            treatment must be determined according to applicable laws and the specific
            transaction.
          </p>
        </div>
      </div>
    </section>
  );
}


/* ── 3 · Benefits strip ───────────────────────────── */
function BenefitsStrip() {
  return (
    <section className="relative overflow-hidden bg-ink">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(112,217,208,0.09),transparent_60%)]" aria-hidden="true" />
      <div className="relative mx-auto flex max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b) => (
            <div key={b.label} className="flex gap-3.5 pb-5 last:border-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.1] bg-white/[0.05]">
                <b.icon className="h-4 w-4 text-teal" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-fog">{b.label}</p>
                <p className="mt-1 text-xs leading-5 text-mist">{b.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 4 · Features ─────────────────────────────────── */
function Features() {
  const tones = ['text-blue', 'text-teal', 'text-gold'];
  return (
    <section id="features" className="scroll-mt-24 bg-ivory py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">Core capabilities</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            Everything You Need to Run Your Business
          </h2>
          <p className="mt-4 text-base leading-7 text-mist">
            Complete toolkit for billing, inventory, invoicing, and business management.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <article
              key={f.title}
              className="flex gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.04] p-6 transition-colors hover:border-white/[0.12]"
            >
              <f.icon className={`h-5 w-5 shrink-0 ${tones[i]}`} aria-hidden />
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-ink">{f.title}</h3>
                <p className="mt-2 text-sm leading-6 text-mist">{f.description}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ── 5 · Business audience showcase ───────────────── */
function Businesses() {
  return (
    <section id="business" className="bg-ink relative scroll-mt-24 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_100%,rgba(112,217,208,0.10),transparent_55%)]" aria-hidden="true" />
      <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">Designed for small businesses</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-fog sm:text-4xl">
            Built for Everyday Businesses
          </h2>
          <p className="mt-4 text-base leading-7 text-mist">
            From retail shops to service providers — DNR scales with your business.
          </p>
        </div>
        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          <div className="min-w-0 lg:col-span-3">
            <div className="grid gap-3 sm:grid-cols-2">
              {BUSINESSES.map((b) => (
                <div key={b.label} className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                  <b.icon className="h-4 w-4 shrink-0 text-teal" aria-hidden />
                  <span className="text-sm font-medium text-fog">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid min-w-0 content-start gap-4 sm:grid-cols-2 lg:col-span-2">
            {DOCUMENT_TYPES.map((d) => (
              <div key={d.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <div className="flex items-center gap-2.5">
                  <d.icon className="h-4 w-4 shrink-0 text-gold" aria-hidden />
                  <span className="text-xs font-bold text-fog">{d.label}</span>
                </div>
                <p className="mt-2 text-xs leading-5 text-mist/70">Draft, preview, and issue from one place.</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 6 · Workflow ─────────────────────────────────── */
function Workflow() {
  const steps = [
    { name: 'Billing', icon: IndianRupee },
    { name: 'Inventory', icon: Package },
    { name: 'Invoices', icon: FileText },
    { name: 'Business Management', icon: LayoutGrid },
  ];
  return (
    <section id="workflow" className="scroll-mt-24 bg-ivory py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">Complete product suite</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            Billing · Inventory · Invoices · Business Management
          </h2>
          <p className="mt-4 text-base leading-7 text-mist">
            Four integrated modules that cover your entire business workflow.
          </p>
        </div>
        <div className="mt-12">
          <ol className="flex flex-wrap items-center gap-3">
            {steps.map((s, i) => (
              <li key={s.name} className="relative flex flex-col items-center gap-2">
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-sm font-bold text-fog">
                  <s.icon className="h-4 w-4" aria-hidden />
                </div>
                <span className="text-xs font-semibold text-fog">{s.name}</span>
                {i < steps.length - 1 && (
                  <div className="absolute top-6 left-6 flex-1 h-px bg-white/[0.14]" aria-hidden="true" />
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}


/* ── 7 · Free to use for now ──────────────────────── */
function FreeOffer() {
  return (
    <section id="free" className="relative scroll-mt-24 overflow-hidden bg-[#0B1C32]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(112,217,208,0.16),transparent_55%)]" aria-hidden="true" />
      <div className="relative mx-auto flex flex-col items-center justify-between gap-8 px-4 py-16 sm:px-6 lg:px-8 sm:py-20 lg:flex-row">
        <div className="max-w-xl text-center sm:text-left">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Free to use for now
          </p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-fog sm:text-4xl">
            Free to use for now.
          </h2>
          <p className="mt-3 max-w-md text-sm leading-6 text-mist">
            Get started with the current version at no cost. Some features may still be in
            development. We will communicate any future pricing changes before they take effect.
          </p>
        </div>
        <a
          href="#/signup"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink transition-colors hover:bg-[#b89766]"
        >
          Get Started
          <ArrowRight className="h-4 w-4" aria-hidden />
        </a>
      </div>
    </section>
  );
}

/* ── 8 · Footer ───────────────────────────────────── */
function Footer() {
  return (
    <footer className="bg-[#060D18]">
      <div className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 lg:px-4">
        <div className="flex flex-col items-start justify-between gap-8 sm:flex-row sm:items-start">
          <div className="max-w-xs">
            <a href="#/" className="flex items-center gap-2.5" aria-label="DNR home">
              <img src="/assets/logo.svg" alt="DNR logo" className="h-9 w-9 rounded-xl" />
              <span className="text-base font-extrabold tracking-tight text-fog">DNR</span>
              <span className="text-xs font-medium text-gold uppercase tracking-wider">DIGITAL NET REGISTER</span>
            </a>
            <p className="mt-3 text-sm text-mist">Your Business. Digitally Managed.</p>
            <p className="mt-2 text-xs text-mist/60">Billing · Inventory · Invoices · Business Management</p>
          </div>
          <div className="grid flex-1 gap-8 sm:grid-cols-2">
            <nav className="flex flex-col items-start gap-3 text-sm" aria-label="Footer product">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-mist">Product</span>
              <button type="button" onClick={() => scrollToSection('features')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Features</button>
              <button type="button" onClick={() => scrollToSection('business')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Businesses</button>
              <button type="button" onClick={() => scrollToSection('workflow')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Products</button>
              <button type="button" onClick={() => scrollToSection('free')} className="text-sm font-medium text-mist transition-colors hover:text-fog">Free to use</button>
            </nav>
            <div className="flex flex-col gap-3 text-sm">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-mist">Account</span>
              <a href="#/login" className="text-sm font-medium text-mist transition-colors hover:text-fog">Sign In</a>
              <a href="#/signup" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-bold text-ink transition-colors hover:bg-[#b89766]">
                Get Started
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <a href="mailto:support@dnrvyapar.in" className="inline-flex items-center gap-2 text-sm font-medium text-gold transition-colors hover:text-fog">
                <Mail className="h-4 w-4" aria-hidden />
                support@dnrvyapar.in
              </a>
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-5 border-t border-white/[0.06] pt-6 text-xs text-mist/60">
          <a href="#/legal/privacy" className="transition-colors hover:text-fog">Privacy</a>
          <a href="#/legal/terms" className="transition-colors hover:text-fog">Terms</a>
          <a href="#/legal/cookies" className="transition-colors hover:text-fog">Cookie policy</a>
        </div>
      </div>
    </footer>
  );
}

/* ── Elevation ────────────────────────────────────── */
export function MarketingPage() {
  return (
    <>
      <Navigation />
      <Hero />
      <BenefitsStrip />
      <Features />
      <Businesses />
      <Workflow />
      <FreeOffer />
      <Footer />
    </>
  );
}
